import React from "react";
import { withStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const hebrewLabels = {
  title: "סיכום מכירות ומלאי שנתי",
  subtitle: "כל המוצרים שנמכרו מתחילת השנה — הקרנות, מלאי ומה להכניס לקונטיינר הבא",
  refresh: "רענן",
  loading: "טוען נתונים...",
  errorPrefix: "שגיאה בטעינת הנתונים",
  searchPlaceholder: "חפש לפי שם, מותג, EAN...",
  noResults: "לא נמצאו מוצרים",
  summaryCards: {
    productsSold: "מוצרים שנמכרו",
    totalUnits: "יחידות שנמכרו (שנה נוכחית)",
    totalRevenue: "הכנסה (שנה נוכחית)",
    inDeficit: "מוצרים בחסר",
    totalToBuy: "יחידות מומלצות לרכישה",
  },
  tableHeaders: {
    product: "מוצר",
    ytdUnits: "יחידות שנמכרו",
    projectedEOY: "תחזית סוף שנה",
    remainingDemand: "ביקוש נותר",
    currentStock: "מלאי נוכחי",
    deficit: "חסר",
    recommendedPurchase: "מומלץ לרכישה",
    status: "סטטוס",
    confidence: "רמת ביטחון",
    daysOfStock: "ימי מלאי",
  },
  status: {
    deficit: "חסר",
    sufficient: "מספיק",
    overstock: "עודף",
  },
  confidence: {
    high: "גבוה",
    medium: "בינוני",
    low: "נמוך",
  },
  viewProduct: "פתח אנליטיקה מלאה",
};

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    direction: "rtl",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  searchField: {
    minWidth: 280,
    flex: 1,
  },
  summaryGrid: {
    marginBottom: theme.spacing(3),
  },
  summaryCard: {
    height: "100%",
  },
  summaryCardContent: {
    textAlign: "center",
  },
  summaryNumber: {
    fontSize: "1.9rem",
    fontWeight: "bold",
    lineHeight: 1.15,
    color: theme.palette.primary.main,
  },
  summaryNumberDanger: {
    fontSize: "1.9rem",
    fontWeight: "bold",
    lineHeight: 1.15,
    color: theme.palette.error.main,
  },
  summaryLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  tableContainer: {
    maxHeight: "calc(100vh - 360px)",
    minHeight: 300,
  },
  tableHead: {
    backgroundColor: theme.palette.grey[50],
  },
  productCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 260,
  },
  productName: {
    fontSize: "0.85rem",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  productMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  avatar: {
    width: 40,
    height: 40,
    flexShrink: 0,
    backgroundColor: theme.palette.grey[200],
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },
  chipDeficit: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  chipSufficient: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  chipOverstock: {
    backgroundColor: "#fff8e1",
    color: "#f57f17",
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  recommendedCell: {
    fontWeight: 700,
    color: theme.palette.error.dark,
  },
  recommendedZero: {
    color: theme.palette.text.disabled,
  },
  viewLink: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    padding: 4,
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.04)",
    },
  },
  numericCell: {
    textAlign: "left",
    fontVariantNumeric: "tabular-nums",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 240,
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  errorBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
    flexDirection: "column",
    gap: theme.spacing(1),
    color: theme.palette.error.main,
  },
});

const SORT_COLS = [
  { id: "ytdUnits", label: hebrewLabels.tableHeaders.ytdUnits },
  { id: "projectedEndOfYearUnits", label: hebrewLabels.tableHeaders.projectedEOY },
  { id: "remainingDemand", label: hebrewLabels.tableHeaders.remainingDemand },
  { id: "currentStock", label: hebrewLabels.tableHeaders.currentStock },
  { id: "deficit", label: hebrewLabels.tableHeaders.deficit },
  { id: "recommendedPurchase", label: hebrewLabels.tableHeaders.recommendedPurchase },
  { id: "daysOfStockRemaining", label: hebrewLabels.tableHeaders.daysOfStock },
];

class SalesStockSummary extends React.Component {
  state = {
    rows: [],
    loading: false,
    error: null,
    search: "",
    sortCol: "ytdUnits",
    sortDir: "desc",
  };

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(
        `${basePath}/api/v1/admin/product-analytics/ytd-sales-summary`,
        { headers: utils.getAdminAuthHeaders() }
      );
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Unknown error");
      this.setState({ rows: data.rows || [] });
    } catch (err) {
      this.setState({ error: err.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSort = (col) => {
    const { sortCol, sortDir } = this.state;
    if (sortCol === col) {
      this.setState({ sortDir: sortDir === "desc" ? "asc" : "desc" });
    } else {
      this.setState({ sortCol: col, sortDir: "desc" });
    }
  };

  getFilteredRows = () => {
    const { rows, search, sortCol, sortDir } = this.state;
    const q = search.trim().toLowerCase();

    let filtered = q
      ? rows.filter(
          (r) =>
            (r.name || "").toLowerCase().includes(q) ||
            (r.brand || "").toLowerCase().includes(q) ||
            (r.ean || "").toLowerCase().includes(q) ||
            (r.size || "").toLowerCase().includes(q)
        )
      : rows;

    return [...filtered].sort((a, b) => {
      const av = a[sortCol] ?? -Infinity;
      const bv = b[sortCol] ?? -Infinity;
      return sortDir === "desc" ? bv - av : av - bv;
    });
  };

  getSummary = () => {
    const { rows } = this.state;
    const totalUnits = rows.reduce((s, r) => s + (r.ytdUnits || 0), 0);
    const totalRevenue = rows.reduce((s, r) => s + (r.ytdRevenue || 0), 0);
    const inDeficit = rows.filter((r) => r.status === "deficit").length;
    const totalToBuy = rows.reduce((s, r) => s + (r.recommendedPurchase || 0), 0);
    return { productsSold: rows.length, totalUnits, totalRevenue, inDeficit, totalToBuy };
  };

  formatNumber = (n) =>
    n == null ? "—" : Math.round(n).toLocaleString("he-IL");

  formatCurrency = (n) =>
    n == null ? "—" : `₪${Math.round(n).toLocaleString("he-IL")}`;

  navigateToProduct = (ean) => {
    if (typeof window !== "undefined") {
      window.open(`/admin/product-analytics?ean=${encodeURIComponent(ean)}&tab=2`, "_blank");
    }
  };

  renderStatusChip = (status) => {
    const { classes } = this.props;
    const chipClass =
      status === "deficit"
        ? classes.chipDeficit
        : status === "overstock"
        ? classes.chipOverstock
        : classes.chipSufficient;
    return (
      <Chip
        size="small"
        label={hebrewLabels.status[status] || status}
        className={chipClass}
      />
    );
  };

  renderSummaryCards = () => {
    const { classes } = this.props;
    const { rows } = this.state;
    const s = this.getSummary();
    const cards = [
      { label: hebrewLabels.summaryCards.productsSold, value: this.formatNumber(s.productsSold), danger: false },
      { label: hebrewLabels.summaryCards.totalUnits, value: this.formatNumber(s.totalUnits), danger: false },
      { label: hebrewLabels.summaryCards.totalRevenue, value: this.formatCurrency(s.totalRevenue), danger: false },
      { label: hebrewLabels.summaryCards.inDeficit, value: this.formatNumber(s.inDeficit), danger: s.inDeficit > 0 },
      { label: hebrewLabels.summaryCards.totalToBuy, value: this.formatNumber(s.totalToBuy), danger: s.totalToBuy > 0 },
    ];
    return (
      <Grid container spacing={2} className={classes.summaryGrid}>
        {cards.map((c) => (
          <Grid item xs={12} sm={6} md key={c.label}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent className={classes.summaryCardContent}>
                <Typography className={c.danger ? classes.summaryNumberDanger : classes.summaryNumber}>
                  {rows.length === 0 ? "—" : c.value}
                </Typography>
                <Typography className={classes.summaryLabel}>{c.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  renderTableHead = () => {
    const { classes } = this.props;
    const { sortCol, sortDir } = this.state;
    return (
      <TableHead className={classes.tableHead}>
        <TableRow>
          <TableCell style={{ minWidth: 280 }}>
            {hebrewLabels.tableHeaders.product}
          </TableCell>
          {SORT_COLS.map((col) => (
            <TableCell key={col.id} align="center" style={{ minWidth: 110, whiteSpace: "nowrap" }}>
              <TableSortLabel
                active={sortCol === col.id}
                direction={sortCol === col.id ? sortDir : "desc"}
                onClick={() => this.handleSort(col.id)}
              >
                {col.label}
              </TableSortLabel>
            </TableCell>
          ))}
          <TableCell align="center" style={{ minWidth: 90 }}>
            {hebrewLabels.tableHeaders.status}
          </TableCell>
          <TableCell align="center" style={{ minWidth: 100 }}>
            {hebrewLabels.tableHeaders.confidence}
          </TableCell>
          <TableCell style={{ width: 48 }} />
        </TableRow>
      </TableHead>
    );
  };

  renderRow = (row) => {
    const { classes } = this.props;

    const daysDisplay =
      row.daysOfStockRemaining == null
        ? "∞"
        : row.daysOfStockRemaining > 365
        ? ">365"
        : this.formatNumber(row.daysOfStockRemaining);

    return (
      <TableRow key={row.ean} hover>
        <TableCell>
          <div className={classes.productCell}>
            <Avatar
              src={row.imageUrl || undefined}
              alt={row.name}
              className={classes.avatar}
              variant="rounded"
            >
              {!row.imageUrl && (row.brand || row.name || "?").charAt(0)}
            </Avatar>
            <div>
              <div className={classes.productName}>{row.name}</div>
              <div className={classes.productMeta}>
                {[row.brand, row.size, row.ean].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
        </TableCell>

        <TableCell align="center" className={classes.numericCell}>
          <strong>{this.formatNumber(row.ytdUnits)}</strong>
        </TableCell>

        <TableCell align="center" className={classes.numericCell}>
          {this.formatNumber(row.projectedEndOfYearUnits)}
        </TableCell>

        <TableCell align="center" className={classes.numericCell}>
          {this.formatNumber(row.remainingDemand)}
        </TableCell>

        <TableCell align="center" className={classes.numericCell}>
          {this.formatNumber(row.currentStock)}
        </TableCell>

        <TableCell align="center" className={classes.numericCell}>
          {row.deficit > 0 ? (
            <span style={{ color: "#c62828", fontWeight: 600 }}>
              {this.formatNumber(row.deficit)}
            </span>
          ) : (
            <span style={{ color: "#9e9e9e" }}>—</span>
          )}
        </TableCell>

        <TableCell align="center" className={classes.numericCell}>
          {row.recommendedPurchase > 0 ? (
            <strong className={classes.recommendedCell}>
              {this.formatNumber(row.recommendedPurchase)}
            </strong>
          ) : (
            <span className={classes.recommendedZero}>—</span>
          )}
        </TableCell>

        <TableCell align="center" className={classes.numericCell}>
          <Tooltip title={`${hebrewLabels.tableHeaders.daysOfStock}: ${daysDisplay}`}>
            <span>{daysDisplay}</span>
          </Tooltip>
        </TableCell>

        <TableCell align="center">
          {this.renderStatusChip(row.status)}
        </TableCell>

        <TableCell align="center">
          {hebrewLabels.confidence[row.confidence] || row.confidence || "—"}
        </TableCell>

        <TableCell align="center">
          <Tooltip title={hebrewLabels.viewProduct}>
            <Button
              className={classes.viewLink}
              size="small"
              onClick={() => this.navigateToProduct(row.ean)}
            >
              <OpenInNewIcon fontSize="small" />
            </Button>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  render() {
    const { classes } = this.props;
    const { loading, error, search } = this.state;
    const filteredRows = this.getFilteredRows();

    return (
      <div className={classes.root}>
        {/* Controls */}
        <div className={classes.controls}>
          <TextField
            className={classes.searchField}
            size="small"
            variant="outlined"
            placeholder={hebrewLabels.searchPlaceholder}
            value={search}
            onChange={(e) => this.setState({ search: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={this.loadData}
            disabled={loading}
          >
            {hebrewLabels.refresh}
          </Button>
        </div>

        {/* Summary cards */}
        {!error && this.renderSummaryCards()}

        {/* Loading */}
        {loading && (
          <Box className={classes.loadingBox}>
            <CircularProgress />
            <Typography color="textSecondary">{hebrewLabels.loading}</Typography>
          </Box>
        )}

        {/* Error */}
        {!loading && error && (
          <Box className={classes.errorBox}>
            <WarningIcon />
            <Typography>
              {hebrewLabels.errorPrefix}: {error}
            </Typography>
          </Box>
        )}

        {/* Table */}
        {!loading && !error && (
          <Paper variant="outlined">
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader size="small">
                {this.renderTableHead()}
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" style={{ padding: 32 }}>
                        <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 8 }}>
                          <CheckCircleOutlineIcon color="disabled" />
                          <Typography color="textSecondary">{hebrewLabels.noResults}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => this.renderRow(row))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box px={2} py={1} display="flex" justifyContent="flex-end">
              <Typography variant="caption" color="textSecondary">
                {filteredRows.length.toLocaleString("he-IL")} מוצרים
              </Typography>
            </Box>
          </Paper>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(SalesStockSummary);
